import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  CalendarPlus,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import {
  bookingIcsUrl,
  cancelBooking,
  getAvailability,
  getBooking,
  rescheduleBooking,
} from '@/services/bookingService';
import { useSeo } from '@/lib/seo';
import { trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';

const pad = (n) => String(n).padStart(2, '0');

const dayLabel = (dayKey) =>
  new Intl.DateTimeFormat('en-IE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'Europe/Dublin',
  }).format(new Date(`${dayKey}T12:00:00Z`));

// Secure booking-management page (cancel / reschedule / calendar invite).
// Token-gated, unguessable links, never indexed (noindex, nofollow).
export default function BookingManagePage() {
  const { reference } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [state, setState] = useState('loading'); // loading | ready | error | cancelled
  const [booking, setBooking] = useState(null);
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState(false);

  // Reschedule picker state
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${pad(now.getMonth() + 1)}`);
  const [availability, setAvailability] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  const [newDay, setNewDay] = useState(null);
  const [newSlot, setNewSlot] = useState(null);

  useSeo({
    title: 'Manage your booking | Amden Group',
    description: 'Secure booking management.',
    robots: 'noindex, nofollow',
  });

  const load = useCallback(async () => {
    try {
      const res = await getBooking(reference, token);
      setBooking(res.booking);
      setState(res.booking.status === 'cancelled' ? 'cancelled' : 'ready');
    } catch {
      setState('error');
    }
  }, [reference, token]);

  useEffect(() => {
    load();
  }, [load]);

  const loadAvailability = useCallback(async () => {
    if (!booking) return;
    setAvailLoading(true);
    try {
      setAvailability(await getAvailability('initial-consultation', month));
    } catch {
      setAvailability(null);
    } finally {
      setAvailLoading(false);
    }
  }, [booking, month]);

  useEffect(() => {
    if (state === 'ready') loadAvailability();
  }, [state, loadAvailability]);

  const availableDays = useMemo(
    () => Object.keys(availability?.days || {}).sort(),
    [availability]
  );

  const doCancel = async () => {
    if (!window.confirm('Cancel this appointment? You can book another time afterwards.')) return;
    setBusy(true);
    setActionError('');
    try {
      await cancelBooking(reference, { token });
      setState('cancelled');
      trackEvent('booking_cancelled');
    } catch (err) {
      setActionError(err?.response?.data?.detail || 'The booking could not be cancelled. Please contact us directly.');
    } finally {
      setBusy(false);
    }
  };

  const doReschedule = async () => {
    if (!newSlot || busy) return;
    setBusy(true);
    setActionError('');
    try {
      const res = await rescheduleBooking(reference, { token, newStartUtc: newSlot.startUtc });
      trackEvent('booking_rescheduled');
      // The old management link is now used up — move to the new secure link.
      window.location.href = `/booking/manage/${res.reference}?token=${res.managementToken}`;
    } catch (err) {
      if (err?.response?.status === 409) {
        setActionError('That appointment time is no longer available. Please choose another time.');
        setNewSlot(null);
        loadAvailability();
      } else {
        setActionError(err?.response?.data?.detail || 'The booking could not be rescheduled. Please try again.');
      }
      setBusy(false);
    }
  };

  return (
    <section className="bg-mist py-16 sm:py-24" data-testid="booking-manage-page">
      <div className="mx-auto max-w-2xl px-4 sm:px-6">
        <Link to="/contact" className="inline-flex items-center gap-2 text-sm font-bold text-navy-900 underline-offset-2 hover:underline" data-testid="manage-back-contact">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to Contact
        </Link>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-10">
          {state === 'loading' && (
            <div className="flex items-center justify-center py-14" role="status">
              <Loader2 className="h-6 w-6 animate-spin text-navy-900" aria-hidden="true" />
              <span className="sr-only">Loading your booking</span>
            </div>
          )}

          {state === 'error' && (
            <div className="py-10 text-center" role="alert" data-testid="manage-error">
              <AlertCircle className="mx-auto h-8 w-8 text-red-600" aria-hidden="true" />
              <h1 className="mt-4 text-xl font-extrabold text-navy-900">This booking link is invalid or has expired</h1>
              <p className="mt-2 text-sm text-slate-600">
                If you need to change an appointment, contact us at info@amden.ie or 046 927 4424.
              </p>
            </div>
          )}

          {state === 'cancelled' && (
            <div className="py-6 text-center" role="status" data-testid="manage-cancelled">
              <CheckCircle2 className="mx-auto h-8 w-8 text-teal-700" aria-hidden="true" />
              <h1 className="mt-4 text-xl font-extrabold text-navy-900">Appointment cancelled</h1>
              <p className="mt-2 text-sm text-slate-600">
                Booking <span className="font-mono font-bold">{reference}</span> has been cancelled.
              </p>
              <Link
                to="/contact#book-appointment"
                data-testid="manage-rebook-link"
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-navy-900 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-navy-700"
              >
                Book Another Time
              </Link>
            </div>
          )}

          {state === 'ready' && booking && (
            <>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Your appointment</p>
              <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-navy-900" data-testid="manage-heading">
                {booking.meetingType.name}
              </h1>
              <dl className="mt-6 grid gap-x-8 gap-y-4 rounded-xl border border-slate-200 bg-mist p-6 sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Reference</dt>
                  <dd className="mt-1 font-mono text-sm font-bold text-navy-900" data-testid="manage-reference">{booking.reference}</dd>
                </div>
                <div>
                  <dt className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Status</dt>
                  <dd className="mt-1 text-sm font-bold capitalize text-teal-700" data-testid="manage-status">{booking.status}</dd>
                </div>
                <div>
                  <dt className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Date</dt>
                  <dd className="mt-1 text-sm font-bold text-navy-900" data-testid="manage-date">{dayLabel(booking.date)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Time</dt>
                  <dd className="mt-1 text-sm font-bold text-navy-900" data-testid="manage-time">
                    {booking.startTime} – {booking.endTime} (Irish time)
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Format</dt>
                  <dd className="mt-1 text-sm font-bold text-navy-900">{booking.location}</dd>
                </div>
              </dl>

              {actionError && (
                <p role="alert" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-red-700" data-testid="manage-action-error">
                  <AlertCircle className="h-4 w-4" aria-hidden="true" /> {actionError}
                </p>
              )}

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href={bookingIcsUrl(reference, token)}
                  data-testid="manage-add-calendar"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-navy-900 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-navy-700"
                >
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" /> Add to Calendar
                </a>
                <button
                  type="button"
                  onClick={doCancel}
                  disabled={busy}
                  data-testid="manage-cancel-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-md border-2 border-slate-200 px-6 py-3 text-sm font-bold text-slate-600 transition-colors hover:border-red-200 hover:text-red-700 disabled:opacity-50"
                >
                  Cancel Appointment
                </button>
              </div>

              <div id="reschedule" className="mt-10 scroll-mt-28 border-t border-slate-200 pt-8">
                <h2 className="text-lg font-extrabold tracking-tight text-navy-900">Reschedule</h2>
                <p className="mt-1.5 text-sm text-slate-600">
                  Choose another available time (Irish time, Europe/Dublin).
                </p>

                <div className="mt-5 flex items-center justify-between">
                  <button
                    type="button"
                    aria-label="Previous month"
                    disabled={!availability || month <= availability.minMonth}
                    onClick={() => setMonth((m) => `${+m.slice(0, 4) - (m.slice(5) === '01' ? 1 : 0)}-${m.slice(5) === '01' ? '12' : pad(+m.slice(5) - 1)}`)}
                    data-testid="manage-prev-month"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-navy-900 transition-colors hover:bg-navy-50 disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <p className="text-sm font-extrabold text-navy-900" aria-live="polite">{availability?.monthLabel || '…'}</p>
                  <button
                    type="button"
                    aria-label="Next month"
                    disabled={!availability || month >= availability.maxMonth}
                    onClick={() => setMonth((m) => `${m.slice(5) === '12' ? +m.slice(0, 4) + 1 : +m.slice(0, 4)}-${m.slice(5) === '12' ? '01' : pad(+m.slice(5) + 1)}`)}
                    data-testid="manage-next-month"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-navy-900 transition-colors hover:bg-navy-50 disabled:opacity-40"
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>

                {availLoading ? (
                  <div className="flex justify-center py-10" role="status">
                    <Loader2 className="h-5 w-5 animate-spin text-navy-900" aria-hidden="true" />
                  </div>
                ) : availableDays.length === 0 ? (
                  <p className="mt-4 text-sm text-slate-500" data-testid="manage-no-slots">
                    No alternative times are currently open this month.
                  </p>
                ) : (
                  <>
                    <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Available days">
                      {availableDays.map((d) => (
                        <button
                          key={d}
                          type="button"
                          aria-pressed={newDay === d}
                          onClick={() => { setNewDay(d); setNewSlot(null); }}
                          data-testid={`manage-day-${d}`}
                          className={cn(
                            'rounded-md border-2 px-3 py-2 text-xs font-bold transition-colors',
                            newDay === d ? 'border-navy-900 bg-navy-900 text-white' : 'border-slate-200 bg-white text-navy-900 hover:border-navy-300'
                          )}
                        >
                          {dayLabel(d)}
                        </button>
                      ))}
                    </div>
                    {newDay && (
                      <div className="mt-4 flex flex-wrap gap-2" role="radiogroup" aria-label="Available times">
                        {availability.days[newDay].map((s) => (
                          <button
                            key={s.startUtc}
                            type="button"
                            role="radio"
                            aria-checked={newSlot?.startUtc === s.startUtc}
                            onClick={() => setNewSlot(s)}
                            data-testid={`manage-time-${s.time.replace(':', '')}`}
                            className={cn(
                              'rounded-md border-2 px-3 py-2 text-sm font-bold transition-colors',
                              newSlot?.startUtc === s.startUtc ? 'border-navy-900 bg-navy-900 text-white' : 'border-slate-200 bg-white text-navy-900 hover:border-navy-300'
                            )}
                          >
                            {s.time}
                          </button>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={doReschedule}
                      disabled={!newSlot || busy}
                      data-testid="manage-reschedule-confirm"
                      className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-gold-600 px-6 py-3 text-sm font-bold text-navy-950 transition-colors hover:bg-gold-500 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
                    >
                      {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                      Confirm New Time
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
